import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentJointContactInfoDataInput,
  type EnrollmentJointContactInfoData,
  type EnrollmentJointContactInfoSubmitInput,
  type EnrollmentJointContactInfoSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Joint ContactInfo data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointContactInfoData>
 * @example const response = await getEnrollmentJointContactInfoData()
 */
export const getEnrollmentJointContactInfoData = async (
  enrollmentContactInfoSubmitInput?: EnrollmentJointContactInfoDataInput,
  context?: DefaultContext,
): Promise<EnrollmentJointContactInfoData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentJointContactInfoData',
      query: `mutation getEnrollmentJointContactInfoData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          isError
          errors
          accountType
          activePerson {
            applicationId
            path
            progress
            isInitiator
            email
            name {
              firstName
              lastName
            }
            phone {
              countryPhoneCode
              phoneNumber
            }
          }
          jointPerson {
            email
            name {
              firstName
              lastName
            }
            phone {
              countryPhoneCode
              phoneNumber
            }
          }
        }
      }`,
    },
    {
      input: enrollmentContactInfoSubmitInput ?? {},
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointContactInfoData }
  return response.submitJointEnrollment
}

export const useGetEnrollmentJointContactInfoData = createQueryHook<
  EnrollmentJointContactInfoDataInput,
  EnrollmentJointContactInfoData
>(getEnrollmentJointContactInfoData)

/**
 * Submit Enrollment Joint ContactInfo data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointContactInfoSubmitOutput>
 * @example const response = await submitEnrollmentJointContactInfoData();
 */
export const submitEnrollmentJointContactInfoData = async (
  enrollmentContactInfoSubmitInput: EnrollmentJointContactInfoSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointContactInfoSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointContactInfoData',
      query: `mutation submitEnrollmentJointContactInfoData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          isError
          errors
          activePerson {
            path
          }
        }
      }`,
    },
    {
      input: enrollmentContactInfoSubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointContactInfoSubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointContactInfoData = createMutationHook<
  EnrollmentJointContactInfoSubmitInput,
  EnrollmentJointContactInfoSubmitOutput
>(submitEnrollmentJointContactInfoData)
